import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getIndustrialRiskCostsSummary } from '../services/industrialRiskService';

import { esNumberFormat } from '../../industrialCost/utils/numberFormat';
type CostsSummaryResponse = {
  status?: string;
  period_key?: string;
  headline?: {
    annual_cost_eur?: number | null;
    absorbed_cost_eur?: number | null;
    idle_unabsorbed_cost_eur?: number | null;
    activity_ratio?: number | null;
    days_planned?: number | null;
    days_worked?: number | null;
    n_centers?: number | null;
    n_lots_costed?: number | null;
    avg_absorbed_cost_per_kg_input?: number | null;
    avg_absorbed_cost_per_kg_output?: number | null;
    coverage_ratio_actual_vs_expected?: number | null;
  } | null;
  centers?: Array<{
    center_name?: string | null;
    driver_type?: string | null;
    driver_total?: number | null;
    annual_cost_eur?: number | null;
    tariff_eur_per_driver?: number | null;
    alpha_variable?: number | null;
    absorbed_cost_eur?: number | null;
    idle_unabsorbed_cost_eur?: number | null;
  }> | null;
  suppliers?: Array<{
    supplier?: string | null;
    n_lots?: number | null;
    kg_input?: number | null;
    kg_output?: number | null;
    absorbed_cost_eur?: number | null;
    cost_per_kg_input?: number | null;
    cost_per_kg_output?: number | null;
  }> | null;
  formats?: Array<{
    format_code?: string | null;
    units_produced?: number | null;
    kg_output?: number | null;
    mix_share?: number | null;
    absorbed_cost_eur?: number | null;
    cost_per_kg_output?: number | null;
    deviation_vs_avg?: number | null;
  }> | null;
  quality_report?: {
    decision_grade?: boolean | null;
    reasons?: string[] | null;
    note?: string | null;
  } | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function eur(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function eurKg(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${eur(value, 2)} /kg`;
}

function fmtNumber(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return esNumberFormat({ maximumFractionDigits: 0 }).format(value);
}

function pct01(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function pctSigned01(value?: number | null, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const pct = value * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(digits)}%`;
}

function normalizeResponse(raw: unknown): CostsSummaryResponse {
  if (!isRecord(raw)) return {};

  const headlineRaw = isRecord(raw.headline) ? raw.headline : null;
  const qualityReportRaw = isRecord(raw.quality_report) ? raw.quality_report : null;

  const centers = Array.isArray(raw.centers)
    ? raw.centers
        .filter(isRecord)
        .map((item) => ({
          center_name: asString(item.center_name),
          driver_type: asString(item.driver_type),
          driver_total: asNumber(item.driver_total),
          annual_cost_eur: asNumber(item.annual_cost_eur),
          tariff_eur_per_driver: asNumber(item.tariff_eur_per_driver),
          alpha_variable: asNumber(item.alpha_variable),
          absorbed_cost_eur: asNumber(item.absorbed_cost_eur),
          idle_unabsorbed_cost_eur: asNumber(item.idle_unabsorbed_cost_eur),
        }))
    : [];

  const suppliers = Array.isArray(raw.suppliers)
    ? raw.suppliers
        .filter(isRecord)
        .map((item) => ({
          supplier: asString(item.supplier),
          n_lots: asNumber(item.n_lots),
          kg_input: asNumber(item.kg_input),
          kg_output: asNumber(item.kg_output),
          absorbed_cost_eur: asNumber(item.absorbed_cost_eur),
          cost_per_kg_input: asNumber(item.cost_per_kg_input),
          cost_per_kg_output: asNumber(item.cost_per_kg_output),
        }))
    : [];

  const formats = Array.isArray(raw.formats)
    ? raw.formats
        .filter(isRecord)
        .map((item) => ({
          format_code: asString(item.format_code),
          units_produced: asNumber(item.units_produced),
          kg_output: asNumber(item.kg_output),
          mix_share: asNumber(item.mix_share),
          absorbed_cost_eur: asNumber(item.absorbed_cost_eur),
          cost_per_kg_output: asNumber(item.cost_per_kg_output),
          deviation_vs_avg: asNumber(item.deviation_vs_avg),
        }))
    : [];

  return {
    status: asString(raw.status),
    period_key: asString(raw.period_key),
    headline: headlineRaw
      ? {
          annual_cost_eur: asNumber(headlineRaw.annual_cost_eur),
          absorbed_cost_eur: asNumber(headlineRaw.absorbed_cost_eur),
          idle_unabsorbed_cost_eur: asNumber(headlineRaw.idle_unabsorbed_cost_eur),
          activity_ratio: asNumber(headlineRaw.activity_ratio),
          days_planned: asNumber(headlineRaw.days_planned),
          days_worked: asNumber(headlineRaw.days_worked),
          n_centers: asNumber(headlineRaw.n_centers),
          n_lots_costed: asNumber(headlineRaw.n_lots_costed),
          avg_absorbed_cost_per_kg_input: asNumber(headlineRaw.avg_absorbed_cost_per_kg_input),
          avg_absorbed_cost_per_kg_output: asNumber(headlineRaw.avg_absorbed_cost_per_kg_output),
          coverage_ratio_actual_vs_expected: asNumber(headlineRaw.coverage_ratio_actual_vs_expected),
        }
      : undefined,
    centers,
    suppliers,
    formats,
    quality_report: qualityReportRaw
      ? {
          decision_grade:
            typeof qualityReportRaw.decision_grade === 'boolean' ? qualityReportRaw.decision_grade : undefined,
          reasons: Array.isArray(qualityReportRaw.reasons)
            ? qualityReportRaw.reasons.filter((item): item is string => typeof item === 'string')
            : [],
          note: asString(qualityReportRaw.note),
        }
      : undefined,
  };
}

function computeState(data: CostsSummaryResponse): {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
  narrative: string;
} {
  const headline = data.headline;
  const reasons = data.quality_report?.reasons || [];
  const idle = headline?.idle_unabsorbed_cost_eur;
  const activity = headline?.activity_ratio;
  const coverage = headline?.coverage_ratio_actual_vs_expected;

  if (!headline) {
    return {
      label: 'Sin base suficiente',
      tone: 'default',
      narrative: 'Todavía no hay información suficiente para construir una lectura ejecutiva del coste industrial.',
    };
  }

  if ((activity != null && activity < 0.9) || (idle != null && idle > 0)) {
    return {
      label: 'Bajo presión',
      tone: 'warning',
      narrative:
        'Parte del coste anual no se absorbe por igual. La actividad real del periodo cambia la lectura económica del coste observado.',
    };
  }

  if ((coverage != null && coverage < 0.95) || reasons.length > 0) {
    return {
      label: 'Con cautela',
      tone: 'warning',
      narrative:
        'La lectura de costes ya aporta señal útil, pero conviene interpretarla como guía analítica del periodo y no como cierre final.',
    };
  }

  return {
    label: 'Estable',
    tone: 'success',
    narrative:
      'La estructura del coste anual muestra una base utilizable para explicar cómo cambia el coste según lo que realmente se fabricó.',
  };
}

function topCenter(centers: NonNullable<CostsSummaryResponse['centers']>) {
  return [...centers].sort((a, b) => (b.annual_cost_eur || 0) - (a.annual_cost_eur || 0))[0];
}

function topSupplier(suppliers: NonNullable<CostsSummaryResponse['suppliers']>) {
  return [...suppliers].sort((a, b) => (b.absorbed_cost_eur || 0) - (a.absorbed_cost_eur || 0))[0];
}

function lowestCostFormat(formats: NonNullable<CostsSummaryResponse['formats']>) {
  return [...formats]
    .filter((item) => (item.cost_per_kg_output || 0) > 0)
    .sort((a, b) => (a.cost_per_kg_output || 0) - (b.cost_per_kg_output || 0))[0];
}

function highestCostFormat(formats: NonNullable<CostsSummaryResponse['formats']>) {
  return [...formats]
    .filter((item) => (item.cost_per_kg_output || 0) > 0)
    .sort((a, b) => (b.cost_per_kg_output || 0) - (a.cost_per_kg_output || 0))[0];
}

function topMixFormat(formats: NonNullable<CostsSummaryResponse['formats']>) {
  return [...formats].sort((a, b) => (b.mix_share || 0) - (a.mix_share || 0))[0];
}

export default function IndustrialCostsView() {
  const [raw, setRaw] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = useMemo(() => normalizeResponse(raw), [raw]);
  const headline = data.headline;
  const centers = data.centers || [];
  const suppliers = data.suppliers || [];
  const formats = data.formats || [];
  const state = useMemo(() => computeState(data), [data]);
  const mainCenter = useMemo(() => topCenter(centers), [centers]);
  const mainSupplier = useMemo(() => topSupplier(suppliers), [suppliers]);
  const cheapestFormat = useMemo(() => lowestCostFormat(formats), [formats]);
  const mostExpensiveFormat = useMemo(() => highestCostFormat(formats), [formats]);
  const mainMixFormat = useMemo(() => topMixFormat(formats), [formats]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getIndustrialRiskCostsSummary({
          period_key: '2025',
          top_n_suppliers: 8,
        });
        if (!cancelled) setRaw(response);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar la lectura de costes industriales.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2.4,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={1.1}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
                Costes industriales
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                Desglose analítico del coste anual y lectura de cómo cambia según la actividad y lo que realmente se fabricó.
              </Typography>
            </Box>

            <Chip label={state.label} color={state.tone} variant="filled" sx={{ fontWeight: 800 }} />
          </Stack>

          <Typography variant="body1" sx={{ opacity: 0.92 }}>
            {state.narrative}
          </Typography>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="inherit" />
        </Box>
      ) : null}

      {!loading && !error ? (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Coste anual del periodo
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {eur(headline?.annual_cost_eur)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Desglose analítico del coste anual.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Coste absorbido
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {eur(headline?.absorbed_cost_eur)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Parte del coste anual que sí se reparte sobre lo fabricado.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Coste no absorbido
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {eur(headline?.idle_unabsorbed_cost_eur)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Impacto estimado de la menor actividad del periodo.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Coste medio observado
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {eurKg(headline?.avg_absorbed_cost_per_kg_output)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.6 }}>
                  Lectura económica estimada por kg producido.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Actividad observada
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {pct01(headline?.activity_ratio, 0)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {fmtNumber(headline?.days_worked)} días trabajados sobre {fmtNumber(headline?.days_planned)} planificados.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Centro con mayor peso
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {mainCenter?.center_name || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {mainCenter?.annual_cost_eur != null ? `${eur(mainCenter.annual_cost_eur)} de coste anual` : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  Proveedor con más coste absorbido
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                  {mainSupplier?.supplier || '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {mainSupplier?.absorbed_cost_eur != null ? `${eur(mainSupplier.absorbed_cost_eur)} absorbidos` : '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Desglose por centro
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Cómo se reparte el coste anual entre los principales centros industriales.
                    </Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                          Centro
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Coste anual
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Absorbido
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          No absorbido
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                          Variable
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {centers.slice(0, 8).map((row) => (
                        <TableRow key={row.center_name || 'center-row'}>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                            {row.center_name || '—'}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {eur(row.annual_cost_eur)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {eur(row.absorbed_cost_eur)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {eur(row.idle_unabsorbed_cost_eur)}
                          </TableCell>
                          <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                            {pct01(row.alpha_variable, 0)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!centers.length ? (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                            No hay centros disponibles para construir esta lectura.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                }}
              >
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Lectura ejecutiva
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                      Qué explica la media del año y por qué no pesa igual en todos los lotes.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Mensaje principal
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {state.narrative}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Importante
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                      {data.quality_report?.note ||
                        'Lectura económica estimada del coste anual. No equivale al coste contable oficial.'}
                    </Typography>
                  </Box>

                  {(data.quality_report?.reasons || []).length ? (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Observaciones del periodo
                      </Typography>
                      <Stack spacing={0.8} sx={{ mt: 1 }}>
                        {(data.quality_report?.reasons || []).map((reason) => (
                          <Typography key={reason} variant="body2" sx={{ opacity: 0.82 }}>
                            • {reason}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Proveedores principales
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                  Lectura analítica de cómo cambia el coste observado según lo que realmente se fabricó.
                </Typography>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                      Proveedor
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Lotes
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Kg comprados
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Coste absorbido
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Coste por kg comprado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suppliers.slice(0, 8).map((row) => (
                    <TableRow key={row.supplier || 'supplier-row'}>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                        {row.supplier || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {fmtNumber(row.n_lots)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {fmtNumber(row.kg_input)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {eur(row.absorbed_cost_eur)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {eurKg(row.cost_per_kg_input)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {!suppliers.length ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                        No hay proveedores suficientes para construir esta lectura.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Stack spacing={1.8}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Coste observado por formato
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                  Cómo cambia el coste según el mix real de producción. La media anual no pesa igual en todos los formatos.
                </Typography>

                <Box
                  sx={{
                    mt: 1.4,
                    p: 1.5,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Mensaje ejecutivo del mix
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.84, mt: 0.7 }}>
                    {!formats.length
                      ? 'Todavía no hay base suficiente para leer cómo cambia el coste entre formatos.'
                      : cheapestFormat?.cost_per_kg_output != null &&
                        mostExpensiveFormat?.cost_per_kg_output != null &&
                        cheapestFormat.cost_per_kg_output > 0
                      ? (() => {
                          const spread =
                            (mostExpensiveFormat.cost_per_kg_output / cheapestFormat.cost_per_kg_output) - 1;
                          if (spread >= 0.08) {
                            return 'El coste observado cambia de forma relevante entre formatos. La media anual no representa igual todo lo fabricado.';
                          }
                          if (spread >= 0.03) {
                            return 'Se observan diferencias moderadas entre formatos. El mix de producción ya empieza a condicionar la lectura del coste medio.';
                          }
                          return 'En este periodo, el coste observado por formato es relativamente estable, aunque el mix sigue condicionando la media anual.';
                        })()
                      : 'La lectura por formato ya permite comparar cómo se reparte el coste observado sobre la producción real.'}
                  </Typography>

                  <Box
                    sx={{
                      mt: 1.2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.2,
                      py: 0.8,
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>
                      Brecha observada entre formatos
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      {cheapestFormat?.cost_per_kg_output != null &&
                      mostExpensiveFormat?.cost_per_kg_output != null &&
                      cheapestFormat.cost_per_kg_output > 0
                        ? pctSigned01(
                            (mostExpensiveFormat.cost_per_kg_output / cheapestFormat.cost_per_kg_output) - 1,
                            1,
                          )
                        : '—'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                      Menor coste observado
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                      {cheapestFormat?.format_code || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                      {eurKg(cheapestFormat?.cost_per_kg_output)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.4 }}>
                      Peso en mix: {pct01(cheapestFormat?.mix_share, 0)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                      Mayor peso en producción
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                      {mainMixFormat?.format_code || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                      {pct01(mainMixFormat?.mix_share, 0)} del mix observado
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.4 }}>
                      Coste observado: {eurKg(mainMixFormat?.cost_per_kg_output)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                      Mayor coste observado
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.6 }}>
                      {mostExpensiveFormat?.format_code || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.82, mt: 0.5 }}>
                      {eurKg(mostExpensiveFormat?.cost_per_kg_output)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.4 }}>
                      Desviación vs media: {pctSigned01(mostExpensiveFormat?.deviation_vs_avg, 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                      Formato
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Producción observada
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Peso en mix
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Coste absorbido
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Coste por kg producido
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.72)', borderBottomColor: 'rgba(255,255,255,0.08)' }} align="right">
                      Desviación vs media
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formats.slice(0, 8).map((row) => (
                    <TableRow key={row.format_code || 'format-row'}>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: 700 }}>
                        {row.format_code || '—'}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {fmtNumber(row.kg_output)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {pct01(row.mix_share, 0)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {eur(row.absorbed_cost_eur)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {eurKg(row.cost_per_kg_output)}
                      </TableCell>
                      <TableCell sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.88)' }} align="right">
                        {pctSigned01(row.deviation_vs_avg, 0)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {!formats.length ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ borderBottomColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                        No hay base suficiente para construir la lectura por formato en este periodo.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Stack spacing={1.2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Implicación de negocio
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.84 }}>
                {!formats.length
                  ? 'Todavía no hay base suficiente para convertir esta lectura en una pauta de gestión por formato.'
                  : cheapestFormat?.cost_per_kg_output != null &&
                    mostExpensiveFormat?.cost_per_kg_output != null &&
                    cheapestFormat.cost_per_kg_output > 0
                  ? (() => {
                      const spread =
                        (mostExpensiveFormat.cost_per_kg_output / cheapestFormat.cost_per_kg_output) - 1;

                      if (spread >= 0.08) {
                        return 'La media anual deja de ser suficiente como referencia única. Cuando cambie el mix, conviene revisar márgenes, prioridades comerciales y decisiones de producción por formato.';
                      }

                      if (spread >= 0.03) {
                        return 'El mix ya empieza a mover la lectura económica del coste. La media anual sigue siendo útil para seguimiento general, pero no basta para interpretar igual todos los formatos.';
                      }

                      return 'En este periodo, la estructura de coste por formato es relativamente estable. La prioridad no es corregir el mix actual, sino vigilar cambios de actividad y de volumen que puedan mover rápidamente la media observada.';
                    })()
                  : 'La lectura por formato ya permite complementar la media anual con una visión más cercana a lo que realmente se fabricó.'}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                Qué vigilar: cambios de actividad, desplazamientos del mix hacia los formatos con más volumen y decisiones comerciales apoyadas solo en una media anual.
              </Typography>
            </Stack>
          </Box>

          <Typography variant="caption" sx={{ opacity: 0.65 }}>
            Entorno de trabajo orientado a lectura ejecutiva. Esta pantalla muestra una lectura económica estimada del coste anual y no el coste contable oficial.
          </Typography>
        </>
      ) : null}
    </Stack>
  );
}